import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI, downloadFile } from '../../utils/api';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { toast } from 'sonner';
import { ArrowLeft, ChevronLeft, ChevronRight, Download, Users, Zap, UserCheck, Edit2, Check, X, RefreshCw, Calendar, BarChart3 } from 'lucide-react';

const ZoneStaffAttendance = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [staffData, setStaffData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [categoryFilter, setCategoryFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [downloading, setDownloading] = useState(false);
  
  // View mode: 'attendance', 'electrician_perf', or 'supervisor_perf'
  const [viewMode, setViewMode] = useState('attendance');
  const [performanceData, setPerformanceData] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  
  // Day-wise download and performance date
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });
  const [performanceDate, setPerformanceDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });
  const [downloadingDay, setDownloadingDay] = useState(false);
  
  // Message details popup
  const [showMessageDetails, setShowMessageDetails] = useState(false);
  const [messageDetailsData, setMessageDetailsData] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  
  // Edit mode
  const [editingCell, setEditingCell] = useState(null); // { employee_id, date }
  const [saving, setSaving] = useState(false);
  
  const { user, userType, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const hasFetched = useRef(false);

  // Auth check only
  useEffect(() => {
    if (authLoading) return;
    if (!user || userType !== 'admin') {
      navigate('/admin/login');
    }
  }, [authLoading, user, userType, navigate]);

  // Initial fetch - runs once
  useEffect(() => {
    if (authLoading || !user || userType !== 'admin') return;
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchAttendance();
  }, [authLoading, user, userType]);

  // Fetch on filter changes (user action)
  const handleFilterChange = (type, value) => {
    if (type === 'month') setCurrentMonth(value);
    else if (type === 'category') setCategoryFilter(value);
    else if (type === 'region') setRegionFilter(value);
  };

  // Re-fetch when filters change (after initial load)
  useEffect(() => {
    if (hasFetched.current) {
      fetchAttendance();
    }
  }, [currentMonth, categoryFilter, regionFilter]);

  const fetchAttendance = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await adminAPI.getZoneStaffAttendance(currentMonth, categoryFilter || null, regionFilter || null);
      setStaffData(response.data.staff || []);
      if (isManualRefresh) {
        toast.success('Data refreshed');
      }
    } catch (error) {
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchAttendance(true);
  };

  // Fetch Daily WhatsApp Performance data
  const fetchDailyPerformance = async (category) => {
    setLoadingPerformance(true);
    try {
      const response = await adminAPI.getZoneStaffWhatsAppDaily(performanceDate, category, regionFilter || null);
      setPerformanceData(response.data);
    } catch (error) {
      toast.error('Failed to load performance data');
    } finally {
      setLoadingPerformance(false);
    }
  };

  // Fetch performance when switching to performance tab or date changes
  useEffect(() => {
    if (viewMode === 'electrician_perf') {
      fetchDailyPerformance('electrician');
    } else if (viewMode === 'supervisor_perf') {
      fetchDailyPerformance('supervisor');
    }
  }, [viewMode, performanceDate, regionFilter]);

  // Fetch message details for a specific employee
  const fetchMessageDetails = async (employeeId, employeeName, filterType = 'all') => {
    setLoadingMessages(true);
    setShowMessageDetails(true);
    try {
      const response = await adminAPI.getZoneStaffWhatsAppMessages(performanceDate, employeeId);
      
      // Filter messages based on type
      let messages = response.data.messages || [];
      if (filterType === 'media') {
        messages = response.data.media_messages || [];
      } else if (filterType === 'text') {
        messages = response.data.text_messages || [];
      }
      
      setMessageDetailsData({
        ...response.data,
        employee_name: employeeName || response.data.employee_name,
        filtered_messages: messages,
        filter_type: filterType
      });
    } catch (error) {
      toast.error('Failed to load message details');
      setShowMessageDetails(false);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleDownload = async (format = 'excel') => {
    setDownloading(true);
    const regionLabel = regionFilter ? regionFilter.toUpperCase() : 'All';
    const categoryLabel = categoryFilter ? categoryFilter.charAt(0).toUpperCase() + categoryFilter.slice(1) : 'All';
    const extension = format === 'pdf' ? 'pdf' : 'xlsx';
    const filename = `Zone_Staff_Attendance_${regionLabel}_${categoryLabel}_${currentMonth}.${extension}`;
    
    await downloadFile(
      adminAPI.downloadZoneStaffAttendance(currentMonth, categoryFilter || null, regionFilter || null, format),
      filename,
      () => toast.success(`Monthly attendance downloaded as ${format.toUpperCase()}`),
      (error) => toast.error(error || 'Failed to download attendance')
    );
    setDownloading(false);
  };

  const handleDownloadDay = async (format = 'excel') => {
    setDownloadingDay(true);
    const regionLabel = regionFilter ? regionFilter.toUpperCase() : 'All';
    const categoryLabel = categoryFilter ? categoryFilter.charAt(0).toUpperCase() + categoryFilter.slice(1) : 'All';
    const extension = format === 'pdf' ? 'pdf' : 'xlsx';
    const filename = `Zone_Staff_Attendance_${selectedDate}_${regionLabel}_${categoryLabel}.${extension}`;
    
    await downloadFile(
      adminAPI.downloadZoneStaffDayAttendance(selectedDate, categoryFilter || null, regionFilter || null, format),
      filename,
      () => toast.success(`Day attendance downloaded as ${format.toUpperCase()}`),
      (error) => toast.error(error || 'Failed to download day attendance')
    );
    setDownloadingDay(false);
  };

  const handleEditAttendance = async (employeeId, date, newStatus) => {
    setSaving(true);
    try {
      await adminAPI.editZoneStaffAttendance({
        employee_id: employeeId,
        date: date,
        status: newStatus
      });
      
      // Update local state
      setStaffData(prev => prev.map(staff => {
        if (staff.employee_id === employeeId) {
          const newAttendance = { ...staff.attendance };
          newAttendance[date] = { status: newStatus, marked_via: 'manual' };
          
          // Recalculate present count and salary
          const presentCount = Object.values(newAttendance).filter(a => a.status === 'Present').length;
          
          return {
            ...staff,
            attendance: newAttendance,
            present_count: presentCount,
            total_salary: presentCount * (staff.salary_per_day || 0)
          };
        }
        return staff;
      }));
      
      toast.success('Attendance updated');
      setEditingCell(null);
    } catch (error) {
      toast.error('Failed to update attendance');
    } finally {
      setSaving(false);
    }
  };

  const navigateMonth = (direction) => {
    const [year, month] = currentMonth.split('-').map(Number);
    let newYear = year;
    let newMonth = month + direction;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setCurrentMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  const getDaysInMonth = () => {
    const [year, month] = currentMonth.split('-').map(Number);
    return new Date(year, month, 0).getDate();
  };

  const formatMonthDisplay = () => {
    const [year, month] = currentMonth.split('-');
    const date = new Date(year, parseInt(month) - 1, 1);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  // Calculate totals
  const totalStaff = staffData.length;
  const totalElectricians = staffData.filter(s => s.category === 'electrician').length;
  const totalSupervisors = staffData.filter(s => s.category === 'supervisor').length;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading attendance data...</p>
        </div>
      </div>
    );
  }

  const daysInMonth = getDaysInMonth();

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-slate-800 text-white p-4 sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/zone-staff" className="p-2 hover:bg-slate-700 rounded-lg">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-xl font-bold">ZONE STAFF ATTENDANCE</h1>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="h-10 px-4 rounded-lg bg-white/20 hover:bg-white/30 disabled:opacity-50 flex items-center gap-2"
            data-testid="refresh-zone-attendance-btn"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="p-4">
        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Month Navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-slate-100 rounded-lg"
                data-testid="prev-month-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="font-bold min-w-[150px] text-center">{formatMonthDisplay()}</span>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-slate-100 rounded-lg"
                data-testid="next-month-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Filters */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="h-10 px-4 rounded-lg border-2 border-slate-300"
              data-testid="category-filter"
            >
              <option value="">All Categories</option>
              <option value="electrician">Electrician</option>
              <option value="supervisor">Supervisor</option>
            </select>

            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="h-10 px-4 rounded-lg border-2 border-slate-300"
              data-testid="region-filter"
            >
              <option value="">All Regions</option>
              <option value="north">North (1-5)</option>
              <option value="central">Central (6-10)</option>
              <option value="south">South (11-15)</option>
            </select>

            <button
              onClick={() => handleDownload('excel')}
              disabled={downloading}
              className="h-10 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold flex items-center gap-2 disabled:opacity-50"
              data-testid="download-excel-btn"
            >
              {downloading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Month Excel
            </button>
            
            <button
              onClick={() => handleDownload('pdf')}
              disabled={downloading}
              className="h-10 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold flex items-center gap-2 disabled:opacity-50"
              data-testid="download-pdf-btn"
            >
              {downloading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Month PDF
            </button>
          </div>
          
          {/* Day-wise Download Section */}
          <div className="mt-4 pt-4 border-t flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-500" />
              <span className="font-semibold text-slate-700">Day-wise Download:</span>
            </div>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="h-10 px-3 border-2 border-slate-300 rounded-lg"
              data-testid="day-date-picker"
            />
            <button
              onClick={() => handleDownloadDay('excel')}
              disabled={downloadingDay}
              className="h-10 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold flex items-center gap-2 disabled:opacity-50"
              data-testid="download-day-excel-btn"
            >
              {downloadingDay ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Day Excel
            </button>
            <button
              onClick={() => handleDownloadDay('pdf')}
              disabled={downloadingDay}
              className="h-10 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-bold flex items-center gap-2 disabled:opacity-50"
              data-testid="download-day-pdf-btn"
            >
              {downloadingDay ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Day PDF
            </button>
          </div>
        </div>

        {/* View Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setViewMode('attendance')}
            className={`px-4 py-2 rounded-lg font-bold flex items-center gap-2 ${
              viewMode === 'attendance' 
                ? 'bg-blue-600 text-white' 
                : 'bg-white text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="attendance-tab"
          >
            <Calendar className="w-4 h-4" />
            Attendance
          </button>
          <button
            onClick={() => setViewMode('electrician_perf')}
            className={`px-4 py-2 rounded-lg font-bold flex items-center gap-2 ${
              viewMode === 'electrician_perf' 
                ? 'bg-yellow-600 text-white' 
                : 'bg-white text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="electrician-perf-tab"
          >
            <Zap className="w-4 h-4" />
            Electrician Performance
          </button>
          <button
            onClick={() => setViewMode('supervisor_perf')}
            className={`px-4 py-2 rounded-lg font-bold flex items-center gap-2 ${
              viewMode === 'supervisor_perf' 
                ? 'bg-green-600 text-white' 
                : 'bg-white text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="supervisor-perf-tab"
          >
            <UserCheck className="w-4 h-4" />
            Supervisor Performance
          </button>
        </div>

        {/* Summary Cards */}
        {viewMode === 'attendance' && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-xl font-bold">{totalStaff}</div>
                <div className="text-xs text-slate-500">Total Staff</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <div className="text-xl font-bold">{totalElectricians}</div>
                <div className="text-xs text-slate-500">Electricians</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <div className="text-xl font-bold">{totalSupervisors}</div>
                <div className="text-xs text-slate-500">Supervisors</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="text-xl font-bold">{staffData.reduce((sum, s) => sum + (s.present_count || 0), 0)}</div>
                <div className="text-xs text-slate-500">Total Present Days</div>
              </div>
            </div>
          </div>
        </div>

        {/* Attendance Calendar Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-slate-50 flex items-center justify-between">
            <h2 className="font-bold">Attendance Calendar</h2>
            <p className="text-sm text-slate-500">Click on P/A to edit</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-2 text-left font-bold sticky left-0 bg-slate-100 z-10 min-w-[120px]">Name</th>
                  <th className="p-2 text-left font-bold min-w-[50px]">Category</th>
                  <th className="p-2 text-left font-bold min-w-[50px]">Zone</th>
                  <th className="p-2 text-left font-bold min-w-[50px]">Region</th>
                  {[...Array(daysInMonth)].map((_, i) => (
                    <th key={i} className="p-2 text-center font-bold min-w-[35px]">{i + 1}</th>
                  ))}
                  <th className="p-2 text-center font-bold min-w-[50px] bg-green-50">Present</th>
                </tr>
              </thead>
              <tbody>
                {staffData.map((staff) => (
                  <tr key={staff.id} className="border-b hover:bg-slate-50">
                    <td className="p-2 font-semibold sticky left-0 bg-white z-10 border-r">
                      <div className="truncate max-w-[120px]" title={staff.name}>{staff.name}</div>
                      <div className="text-xs text-slate-400">{staff.employee_id}</div>
                    </td>
                    <td className="p-2">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                        staff.category === 'electrician' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {staff.category?.charAt(0).toUpperCase()}
                      </span>
                    </td>
                    <td className="p-2 text-center font-semibold text-slate-700">
                      {staff.assigned_zones?.[0] || '-'}
                    </td>
                    <td className="p-2">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                        staff.region === 'north' ? 'bg-blue-100 text-blue-700' :
                        staff.region === 'central' ? 'bg-purple-100 text-purple-700' :
                        'bg-orange-100 text-orange-700'
                      }`}>
                        {staff.region?.charAt(0).toUpperCase()}
                      </span>
                    </td>
                    {[...Array(daysInMonth)].map((_, i) => {
                      const day = i + 1;
                      const dateStr = `${currentMonth}-${String(day).padStart(2, '0')}`;
                      const att = staff.attendance?.[dateStr] || {};
                      const status = att.status || '-';
                      const isEditing = editingCell?.employee_id === staff.employee_id && editingCell?.date === dateStr;
                      
                      return (
                        <td key={i} className="p-1 text-center">
                          {isEditing ? (
                            <div className="flex gap-1 justify-center">
                              <button
                                onClick={() => handleEditAttendance(staff.employee_id, dateStr, 'Present')}
                                disabled={saving}
                                className="w-6 h-6 bg-green-500 text-white rounded text-xs font-bold hover:bg-green-600"
                              >
                                P
                              </button>
                              <button
                                onClick={() => handleEditAttendance(staff.employee_id, dateStr, 'Absent')}
                                disabled={saving}
                                className="w-6 h-6 bg-red-500 text-white rounded text-xs font-bold hover:bg-red-600"
                              >
                                A
                              </button>
                              <button
                                onClick={() => setEditingCell(null)}
                                className="w-6 h-6 bg-slate-300 text-slate-600 rounded text-xs hover:bg-slate-400"
                              >
                                <X className="w-3 h-3 mx-auto" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setEditingCell({ employee_id: staff.employee_id, date: dateStr })}
                              className={`w-7 h-7 rounded font-bold text-xs ${
                                status === 'Present' ? 'bg-green-100 text-green-700 hover:bg-green-200' :
                                status === 'Absent' ? 'bg-red-100 text-red-700 hover:bg-red-200' :
                                'bg-slate-100 text-slate-400 hover:bg-slate-200'
                              }`}
                              title={att.marked_via === 'whatsapp' ? 'Marked via WhatsApp' : att.marked_via === 'manual' ? 'Manually edited' : 'Not marked'}
                            >
                              {status === 'Present' ? 'P' : status === 'Absent' ? 'A' : '-'}
                            </button>
                          )}
                        </td>
                      );
                    })}
                    <td className="p-2 text-center font-bold bg-green-50">{staff.present_count || 0}</td>
                  </tr>
                ))}
                {staffData.length === 0 && (
                  <tr>
                    <td colSpan={daysInMonth + 5} className="p-8 text-center text-slate-500">
                      No staff found. Add zone staff or adjust filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 p-4 bg-white rounded-lg shadow-sm">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span className="font-bold">Legend:</span>
            <span className="flex items-center gap-1">
              <span className="w-6 h-6 bg-green-100 text-green-700 rounded flex items-center justify-center font-bold text-xs">P</span>
              Present
            </span>
            <span className="flex items-center gap-1">
              <span className="w-6 h-6 bg-red-100 text-red-700 rounded flex items-center justify-center font-bold text-xs">A</span>
              Absent
            </span>
            <span className="flex items-center gap-1">
              <span className="w-6 h-6 bg-slate-100 text-slate-400 rounded flex items-center justify-center font-bold text-xs">-</span>
              Not Marked
            </span>
            <span className="text-slate-400">|</span>
            <span className="flex items-center gap-1">
              <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-bold">E</span>
              Electrician
            </span>
            <span className="flex items-center gap-1">
              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs font-bold">S</span>
              Supervisor
            </span>
          </div>
        </div>
          </>
        )}

        {/* WhatsApp Daily Performance View */}
        {(viewMode === 'electrician_perf' || viewMode === 'supervisor_perf') && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Header with Date Picker */}
            <div className={`p-4 border-b ${viewMode === 'electrician_perf' ? 'bg-yellow-50' : 'bg-green-50'}`}>
              <div className="flex flex-wrap items-center justify-between gap-4">
                <h2 className={`font-bold text-lg ${viewMode === 'electrician_perf' ? 'text-yellow-800' : 'text-green-800'}`}>
                  {viewMode === 'electrician_perf' ? 'Electrician' : 'Supervisor'} WhatsApp Performance
                </h2>
                <div className="flex items-center gap-3">
                  <label className="text-sm font-medium text-slate-600">Select Date:</label>
                  <input
                    type="date"
                    value={performanceDate}
                    onChange={(e) => setPerformanceDate(e.target.value)}
                    className="h-10 px-3 rounded-lg border-2 border-slate-300 focus:border-blue-500"
                  />
                  <button
                    onClick={() => viewMode === 'electrician_perf' ? fetchDailyPerformance('electrician') : fetchDailyPerformance('supervisor')}
                    disabled={loadingPerformance}
                    className={`h-10 px-4 rounded-lg font-semibold flex items-center gap-2 ${
                      viewMode === 'electrician_perf' 
                        ? 'bg-yellow-600 hover:bg-yellow-700 text-white' 
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    } disabled:opacity-50`}
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingPerformance ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>
              {performanceData && (
                <div className="mt-3 text-sm text-slate-600">
                  Showing <span className="font-bold">{performanceData.total_staff || 0}</span> {viewMode === 'electrician_perf' ? 'electricians' : 'supervisors'} for <span className="font-bold">{performanceDate}</span>
                </div>
              )}
            </div>

            {loadingPerformance ? (
              <div className="p-8 text-center">
                <div className={`w-8 h-8 border-4 ${viewMode === 'electrician_perf' ? 'border-yellow-600' : 'border-green-600'} border-t-transparent rounded-full animate-spin mx-auto mb-2`} />
                <p className="text-slate-500">Loading performance data...</p>
              </div>
            ) : performanceData?.performance?.length > 0 ? (
              <>
                {/* Simplified Performance Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="p-3 text-left font-bold">#</th>
                        <th className="p-3 text-left font-bold">Name</th>
                        <th className="p-3 text-center font-bold">Zone</th>
                        {viewMode === 'supervisor_perf' && (
                          <th className="p-3 text-center font-bold">Parks Allotted</th>
                        )}
                        <th className="p-3 text-center font-bold">Media</th>
                        <th className="p-3 text-center font-bold">Text</th>
                        <th className="p-3 text-center font-bold">Total</th>
                        {viewMode === 'supervisor_perf' && (
                          <th className="p-3 text-center font-bold">Performance</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {performanceData.performance.map((staff, idx) => (
                        <tr key={staff.employee_id} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-blue-50`}>
                          <td className="p-3 text-slate-500">{idx + 1}</td>
                          <td className="p-3">
                            <div className="font-semibold">{staff.name}</div>
                            <div className="text-xs text-slate-400">{staff.employee_id}</div>
                          </td>
                          <td className="p-3 text-center font-bold text-blue-600">{staff.zone}</td>
                          {viewMode === 'supervisor_perf' && (
                            <td className="p-3 text-center font-bold text-purple-600">{staff.parks_allotted || 0}</td>
                          )}
                          {/* Media messages column */}
                          <td className="p-3 text-center">
                            <button
                              onClick={() => staff.media_count > 0 && fetchMessageDetails(staff.employee_id, staff.name, 'media')}
                              disabled={staff.media_count === 0}
                              className={`px-3 py-1 rounded-full text-sm font-bold transition-all ${
                                staff.media_count > 0 
                                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer hover:scale-105' 
                                  : 'bg-slate-100 text-slate-400 cursor-default'
                              }`}
                              title={staff.media_count > 0 ? 'Click to view media messages' : 'No media'}
                            >
                              {staff.media_count || 0}
                            </button>
                          </td>
                          {/* Text messages column */}
                          <td className="p-3 text-center">
                            <button
                              onClick={() => staff.text_count > 0 && fetchMessageDetails(staff.employee_id, staff.name, 'text')}
                              disabled={staff.text_count === 0}
                              className={`px-3 py-1 rounded-full text-sm font-bold transition-all ${
                                staff.text_count > 0 
                                  ? 'bg-orange-100 text-orange-700 hover:bg-orange-200 cursor-pointer hover:scale-105' 
                                  : 'bg-slate-100 text-slate-400 cursor-default'
                              }`}
                              title={staff.text_count > 0 ? 'Click to view text messages' : 'No text messages'}
                            >
                              {staff.text_count || 0}
                            </button>
                          </td>
                          {/* Total messages column */}
                          <td className="p-3 text-center">
                            <button
                              onClick={() => staff.messages > 0 && fetchMessageDetails(staff.employee_id, staff.name, 'all')}
                              disabled={staff.messages === 0}
                              className={`px-3 py-1 rounded-full text-sm font-bold transition-all ${
                                staff.messages > 0 
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer hover:scale-105' 
                                  : 'bg-slate-100 text-slate-400 cursor-default'
                              }`}
                              title={staff.messages > 0 ? 'Click to view all messages' : 'No messages'}
                            >
                              {staff.messages || 0}
                            </button>
                          </td>
                          {viewMode === 'supervisor_perf' && (
                            <td className="p-3 text-center">
                              <span className={`px-3 py-1 rounded text-sm font-bold ${
                                staff.performance === '100%' ? 'bg-green-100 text-green-700' :
                                staff.performance?.startsWith('>') ? 'bg-blue-100 text-blue-700' :
                                staff.performance === '-' ? 'bg-slate-100 text-slate-400' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {staff.performance || '-'}
                              </span>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Legend */}
                <div className="p-4 border-t bg-slate-50">
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <span className="font-bold">Columns:</span>
                    <span className="flex items-center gap-1">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-bold">Media</span>
                      Photos/Videos
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-bold">Text</span>
                      Text Messages
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-bold">Total</span>
                      All Messages
                    </span>
                    {viewMode === 'supervisor_perf' && (
                      <>
                        <span className="text-slate-300">|</span>
                        <span className="font-bold">Performance:</span>
                        <span className="flex items-center gap-1">
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-bold">100%</span>
                          =Parks
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-bold">&gt;100%</span>
                          &gt;Parks
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-bold">&lt;100%</span>
                          &lt;Parks
                        </span>
                      </>
                    )}
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    Click on any count to view message details with timestamps
                  </div>
                </div>
              </>
            ) : (
              <div className="p-8 text-center text-slate-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-semibold">No data for {performanceDate}</p>
                <p className="text-sm">Select a different date or check if WhatsApp attendance was uploaded</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Message Details Popup */}
      {showMessageDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col">
            <div className={`p-4 border-b rounded-t-xl flex items-center justify-between ${
              messageDetailsData?.filter_type === 'media' ? 'bg-blue-50' :
              messageDetailsData?.filter_type === 'text' ? 'bg-orange-50' : 'bg-green-50'
            }`}>
              <div>
                <h3 className={`font-bold text-lg ${
                  messageDetailsData?.filter_type === 'media' ? 'text-blue-800' :
                  messageDetailsData?.filter_type === 'text' ? 'text-orange-800' : 'text-green-800'
                }`}>
                  {messageDetailsData?.filter_type === 'media' ? 'Media Messages' :
                   messageDetailsData?.filter_type === 'text' ? 'Text Messages' : 'All WhatsApp Messages'}
                </h3>
                {messageDetailsData && (
                  <p className={`text-sm ${
                    messageDetailsData?.filter_type === 'media' ? 'text-blue-600' :
                    messageDetailsData?.filter_type === 'text' ? 'text-orange-600' : 'text-green-600'
                  }`}>
                    {messageDetailsData.employee_name} • {messageDetailsData.date}
                  </p>
                )}
              </div>
              <button
                onClick={() => { setShowMessageDetails(false); setMessageDetailsData(null); }}
                className={`p-2 rounded-lg ${
                  messageDetailsData?.filter_type === 'media' ? 'hover:bg-blue-100 text-blue-600' :
                  messageDetailsData?.filter_type === 'text' ? 'hover:bg-orange-100 text-orange-600' : 
                  'hover:bg-green-100 text-green-600'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {loadingMessages ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className={`w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mb-2 ${
                    messageDetailsData?.filter_type === 'media' ? 'border-blue-600' :
                    messageDetailsData?.filter_type === 'text' ? 'border-orange-600' : 'border-green-600'
                  }`} />
                  <p className="text-slate-500">Loading messages...</p>
                </div>
              ) : (messageDetailsData?.filtered_messages || messageDetailsData?.messages)?.length > 0 ? (
                <div className="space-y-3">
                  {(messageDetailsData?.filtered_messages || messageDetailsData?.messages).map((msg, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className={`text-white text-xs font-bold px-2 py-1 rounded ${
                        messageDetailsData?.filter_type === 'media' ? 'bg-blue-600' :
                        messageDetailsData?.filter_type === 'text' ? 'bg-orange-600' : 'bg-green-600'
                      }`}>
                        {msg.time}
                      </div>
                      <div className="flex-1 text-sm text-slate-700">
                        {msg.content || '<Media>'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <p>No messages found</p>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t bg-slate-50 rounded-b-xl">
              <div className="text-sm text-slate-600 text-center">
                Showing: <span className={`font-bold ${
                  messageDetailsData?.filter_type === 'media' ? 'text-blue-700' :
                  messageDetailsData?.filter_type === 'text' ? 'text-orange-700' : 'text-green-700'
                }`}>
                  {(messageDetailsData?.filtered_messages || messageDetailsData?.messages)?.length || 0}
                </span> 
                {messageDetailsData?.filter_type === 'media' ? ' media' :
                 messageDetailsData?.filter_type === 'text' ? ' text' : ''} messages
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZoneStaffAttendance;
